import userStorage from "../utils/userStorage";

const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      
      // Format: YYYY-MM-DDTHH:mm
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };
  
  const parseDateFromInput = (dateStr, isEndDate = false) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      
      // Format: YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      // If it's an end date, set time to end of day (23:59:59.999)
      if (isEndDate) {
        return `${year}-${month}-${day}T23:59:59.999Z`;
      }
      // For start date, keep it at start of day (00:00:00)
      return `${year}-${month}-${day}T00:00:00.000Z`;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };

  const localDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // get language from userService
      const language = userStorage.getUserField('preferredLanguage');
      if (language) {
        return new Date(dateStr).toLocaleDateString(language);
      } else {
        return new Date(dateStr).toLocaleDateString();
      }
    } catch (error) {
      console.error('Error parsing date:', error);
      return '';
    }
  }

  const localTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const language = userStorage.getUserField('preferredLanguage');
      const options = { hour: '2-digit', minute: '2-digit' }; // Specify options to exclude seconds
      if (language) {
        return new Date(dateStr).toLocaleTimeString(language, options);
      } else {
        return new Date(dateStr).toLocaleTimeString(undefined, options);
      }
    } catch (error) {
      console.error('Error parsing time:', error);
      return '';
    }
  }

  const localDateTime = (dateStr) => {
    return localDate(dateStr) + ' ' + localTime(dateStr);
  }

  const formatDateTimeForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      
      // Format: YYYY-MM-DDTHH:mm
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return '';
    }
  };

  const getRelativeTimeFormatter = (language) => {
    return new Intl.RelativeTimeFormat(language || 'en', {
      numeric: 'auto',
      style: 'long'
    });
  };

  const relativeDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const language = userStorage.getUserField('preferredLanguage') || 'en';
      const formatter = getRelativeTimeFormatter(language);
      const date = new Date(dateStr);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      const diffInMonths = Math.floor(diffInDays / 30);
      const diffInYears = Math.floor(diffInDays / 365);

      // Very recent (within 30 seconds)
      if (Math.abs(diffInSeconds) < 30) return 'just now';

      // Determine the appropriate unit and value
      if (Math.abs(diffInSeconds) < 60) {
        return formatter.format(-Math.floor(diffInSeconds), 'seconds');
      }
      if (Math.abs(diffInMinutes) < 60) {
        return formatter.format(-Math.floor(diffInMinutes), 'minutes');
      }
      if (Math.abs(diffInHours) < 24) {
        return formatter.format(-Math.floor(diffInHours), 'hours');
      }
      // Special case for "yesterday/tomorrow at specific time"
      if (Math.abs(diffInDays) === 1) {
        return diffInDays > 0 
          ? `${formatter.format(-1, 'day')} at ${localTime(dateStr)}`
          : `${formatter.format(1, 'day')} at ${localTime(dateStr)}`;
      }
      if (Math.abs(diffInDays) < 7) {
        return formatter.format(-Math.floor(diffInDays), 'days');
      }
      if (Math.abs(diffInDays) < 30) {
        const weeks = Math.floor(Math.abs(diffInDays) / 7);
        return formatter.format(-Math.sign(diffInDays) * weeks, 'weeks');
      }
      if (Math.abs(diffInMonths) < 12) {
        return formatter.format(-Math.floor(diffInMonths), 'months');
      }
      return formatter.format(-Math.floor(diffInYears), 'years');
    } catch (error) {
      console.error('Error formatting relative date:', error);
      return '';
    }
  };

  // Format date for HTML date input (YYYY-MM-DD)
  const formatDateForHTMLInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      // If it's already in YYYY-MM-DD format, return it
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // Handle numeric timestamp (milliseconds since epoch)
      if (typeof dateStr === 'number') {
        dateStr = new Date(dateStr);
      }
      
      // Handle string date formats
      if (typeof dateStr === 'string') {
        // Try to create a date object
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        
        // If we couldn't parse it as a date, return empty string
        console.error('Could not parse date string:', dateStr);
        return '';
      }
      
      // Handle Date objects
      if (dateStr instanceof Date) {
        if (isNaN(dateStr.getTime())) {
          console.error('Invalid Date object');
          return '';
        }
        
        const year = dateStr.getFullYear();
        const month = String(dateStr.getMonth() + 1).padStart(2, '0');
        const day = String(dateStr.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      }
      
      console.error('Unhandled date format:', dateStr);
      return '';
    } catch (error) {
      console.error('Error formatting date for HTML input:', error);
      return '';
    }
  };

  export { 
    formatDateForInput, 
    parseDateFromInput, 
    localDate, 
    localDateTime, 
    formatDateTimeForInput, 
    relativeDateTime, 
    formatDateForHTMLInput };
